import React, {useEffect, useState} from 'react';
import classnames from 'classnames';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useIntl} from 'react-intl';
import {Button, Modal} from 'react-bootstrap';
import {FormikProvider, useFormik} from 'formik';
import moment from 'moment';
import can from '../../../_common/components/Permissions/can';
import CustomerInfo from './CustomerInfo';
import CustomersFilters from './CustomersFilters';
import CustomersGrid from './CustomersGrid';
import * as actions from '../../_redux/popupConfirmCustomerActions';
import * as invoiceOrdersActions from '../../_redux/invoiceOrdersActions';
import * as actionsOrder from '../../_redux/popupEditRowDataActions';
import {useInvoiceOrdersUIContext} from '../InvoiceOrdersUIContext';
import {showGlobalMessage} from '../../../_common/actions/globalMessage/showGlobalMessage';
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';
import {messages as M} from '../../../../../_metronic/i18n/messages';
import {Validation} from '../../../../../utils';
import styles from './styles.module.scss';
import {checkArrayGetSum} from '../../utils/checkArrayGetSum';

const ConfirmCustomerForm = ({handleCloseModal, agentID, agentName, allowInvoiceDeal}) => {
  const {formatMessage} = useIntl();
  const prefix = 'invoice-orders-popup-create-customer';
  const dispatch = useDispatch();
  const {queryParams} = useInvoiceOrdersUIContext();
  const {dataLoading, popupBasicData} = useSelector(
    (state) => ({
      dataLoading: state.popupEditRowData.dataLoading,
      popupBasicData: state.popupEditRowData.entities,
    }),
    shallowEqual
  );
  const popupGridData = useSelector((state) => state.popupConfirmCustomer.entities, shallowEqual);
  const gridData = Boolean(popupGridData) ? popupGridData.customers : [];

  const popupBasicDataReady = Boolean(popupBasicData) ? popupBasicData : {};
  const {
    _id: invoiceOrderID,
    address,
    contact,
    createdAt,
    customerID,
    customerName,
    emailRecipients,
    idNumber,
    lines,
    phone,
    canCreate,
    totalAmount
  } = popupBasicDataReady;

  useEffect(() => {
    if (popupBasicData.idNumber || popupBasicData.phone || popupBasicData.emailRecipients) {
      dispatch(actions.getPossibleCustomersList(
        popupBasicData.idNumber,
        popupBasicData.phone,
        popupBasicData.emailRecipients
      ));
    }
  }, [popupBasicData]);

  const editInvoiceOrder = async (values, sendToRivhit) => {
    if (values.createNewCustomer) {
      await editInvoiceOrderAndCreateNewCustomer(values, sendToRivhit);
    } else if (values.selectedCustomerID) {
      await editInvoiceOrderAndUseSelectedCustomer(values, sendToRivhit);
    } else {
      await editInvoiceOrderAndFindCustomer(values, sendToRivhit);
    }
  };

  const editInvoiceOrderAndCreateNewCustomer = async (values, sendToRivhit) => {
    const data = {
        invoiceOrderID,
        invoiceOrderData: {
          createdAt: Date.parse(moment(createdAt)),
          customerName,
          customerID: null,
          contact,
          idNumber,
          phone,
          address,
          emailRecipients,
          lines
        },
        sendToRivhit,
        createNewCustomer: true,
      };

    await dispatch(actions.editOrderWithCustomerData(data, agentID));
  };

  const editInvoiceOrderAndFindCustomer = async (values, sendToRivhit) => {
    const data = {
      invoiceOrderID,
      invoiceOrderData: {
        createdAt: Date.parse(moment(createdAt)),
        customerName,
        customerID: null,
        contact,
        idNumber,
        phone,
        address,
        emailRecipients,
        lines
      },
      sendToRivhit,
      createNewCustomer: false,
    };

    await dispatch(actions.editOrderWithCustomerData(data, agentID));
  };

  const editInvoiceOrderAndUseSelectedCustomer = async (values, sendToRivhit) => {
    const data = {
      invoiceOrderID,
      invoiceOrderData: {
        createdAt: Date.parse(moment(createdAt)),
        customerName: values.customerName,
        customerID: values.selectedCustomerID,
        contact,
        idNumber: values.idNumber,
        phone: values.phone,
        address: values.address,
        emailRecipients: '', //no need to rewrite existing emails with selected customer's email
        lines
      },
      sendToRivhit,
      createNewCustomer: false,
    }

    await dispatch(actions.editOrderWithCustomerData(data, agentID));
  };

  const editInvoiceOrderWithCustomerData = async (values, sendToRivhit, type) => {
    try {
      await editInvoiceOrder(values, sendToRivhit);

      if (sendToRivhit) {
        await dispatch(invoiceOrdersActions.sendInvoiceOrdersToRivhit(
          [[type, invoiceOrderID].filter(Boolean).join('-')],
            checkArrayGetSum(lines),
            createdAt ? moment(createdAt).format('DD-MM-YYYY') : '',
          agentID
        ));
      }

      await dispatch(showGlobalMessage(M.REQUEST_ENDED_SUCCESSFULLY));
      await updateTables();
      await handleCloseModal();
    } catch (err) {
      await dispatch(showGlobalMessage(M['-1']));
    }
  };

  const editInvoiceOrderAndMarkDone = async (values) => {
    try {
      await editInvoiceOrder(values, false);
      await dispatch(invoiceOrdersActions.setInvoiceOrderSentManuallyWithoutReduxUpdate(
        invoiceOrderID,
        totalAmount,
        createdAt ? moment(createdAt).format('DD-MM-YYYY') : '',
        agentID
        )
      );
      await dispatch(showGlobalMessage(M.REQUEST_ENDED_SUCCESSFULLY));
      await updateTables();
      await handleCloseModal();
    } catch (err) {
      await dispatch(showGlobalMessage(M['-1']));
    }
  };

  const updateTables = async () => {
    await dispatch(invoiceOrdersActions.getInvoiceOrdersConfirmCustomers(queryParams));
    await dispatch(invoiceOrdersActions.getInvoiceOrdersEditAndSend(queryParams));
  };

  const [valid, setValid] = useState(false);
  useEffect(() => {
    const isValid = Validation.canSendInvoiceOrderToRivhit(new Date(createdAt));
    setValid(isValid);
  }, [createdAt]);

  const formik = useFormik({
    initialValues: {
      customerID: customerID || '',
      customerName: customerName || '',
      idNumber: idNumber || '',
      phone: phone || '',
      email: emailRecipients || '',
      address: address || '',
      selectedCustomerID: null,
      customerAllowInvoiceDeal: true,
      createNewCustomer: false,
      filterByName: '',
      filterByPhone: '',
      filterByEmail: '',
      filterByIdNumber: '',
      filterByCustomerId: '',
    },

    onSubmit: () => {},
    enableReinitialize: true,
  });

  const popupHeader = [
    formatMessage({id: `${prefix}-header`, defaultMessage: 'Customer selection -'}),
    agentName ? `${formatMessage({id: `${prefix}-header-agentName`, defaultMessage: 'Agent name'})} ${agentName}` : '',
    agentID ? `${formatMessage({id: `${prefix}-header-agentID`, defaultMessage: 'Agent id'})} ${agentID}` : '',
  ].filter(Boolean).join(' ');
  const buttonsClassnames = 'btn btn-sm w-200px h-40px';

  return (
    <>
      {dataLoading
        ? (
          <div className={'spinner-container'}>
            <span className={'spinner spinner-md spinner-primary'}/>
          </div>
        )
        : (
          <FormikProvider value={formik}>
            <h3 className="card-title font-weight-bold text-dark text-center mb-2">
              {popupHeader}
            </h3>
            <h6 className="card-title font-weight-bold text-dark text-center mb-1">
              {formatMessage({id: `${prefix}-subheader-from-agent`, defaultMessage: 'Details from agent'})}
            </h6>
            <CustomerInfo/>
            <h6 className="card-title font-weight-bold text-dark text-center mb-1">
              {formatMessage({id: `${prefix}-subheader-from-customers`, defaultMessage: 'Details from customers'})}
            </h6>
            <CustomersFilters
              popupBasicData={popupBasicData}
              formikProps={formik}
            />
            <CustomersGrid
              gridData={gridData}
              formikProps={formik}
            />
          </FormikProvider>
        )
      }
      <div className={styles.buttonsContainer}>
        <Button
          className={classnames('custom-send-button', buttonsClassnames)}
          onClick={() => editInvoiceOrderWithCustomerData(formik.values, false)}
          disabled={dataLoading}
        >
          {formatMessage({id: 'invoice-orders-popup-create-button-save-no-send', defaultMessage: 'Save no send'})}
        </Button>
        <div className={'d-flex flex-column'}>
          <Button
          className={classnames('custom-send-button mb-4', buttonsClassnames)}
          onClick={() => editInvoiceOrderWithCustomerData(formik.values, true, 1)}
          disabled={dataLoading || !canCreate || !valid || !allowInvoiceDeal}
        >
          {formatMessage({id: 'invoice-orders-popup-create-button-save-send', defaultMessage: 'Save send'})}
        </Button>
          <Button
            className={classnames('custom-send-button', buttonsClassnames)}
            onClick={() => editInvoiceOrderWithCustomerData(formik.values, true, 8)}
            disabled={dataLoading || !canCreate || !valid || !allowInvoiceDeal || !formik.values.customerAllowInvoiceDeal}
          >
            {formatMessage({id: 'invoice-orders-popup-create-button-save-send-type8', defaultMessage: 'Save send'})}
          </Button>
        </div>
        <Button
          className={classnames('custom-send-button', buttonsClassnames)}
          onClick={async () => await editInvoiceOrderAndMarkDone(formik.values)}
          disabled={dataLoading}
        >
          {formatMessage({id: 'invoice-orders-popup-create-button-save-markDone', defaultMessage: 'Mark Done'})}
        </Button>
        <Button
          className={classnames('btn-secondary', buttonsClassnames)}
          onClick={handleCloseModal}
        >
          {formatMessage({id: 'invoice-orders-popup-button-cancel', defaultMessage: 'Cancel'})}
        </Button>
      </div>
    </>
  );
};

const PopupConfirmCustomer = ({data}) => {
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const handleCloseModal = () => setShow(false);
  const handleShowModal = () => setShow(true);

  const handleOpenPopup = () => {
    dispatch(actionsOrder.getPopupEditRowData(data._id));
    handleShowModal();
  };

  return (
    <>
      <Button
        className={classnames(
          `btn btn-icon btn-light btn-sm custom-action-button`,
          {[`custom-disabled-button cursor-default`]: !can('full', 'invoiceOrders')}
        )}
        title={formatMessage({id: 'invoice-orders-button-tooltip-createCustomer', defaultMessage: 'Create customer'})}
        disabled={!(can('full', 'invoiceOrders'))}
        onClick={() => handleOpenPopup()}
      >
        <img
          src={toAbsoluteUrl('/media/svg/Add-user_brandColor.svg')}
          alt="Add-customer"
        />
      </Button>
      <Modal show={show} size={'xl'}>
        <Modal.Body>
          <ConfirmCustomerForm
            handleCloseModal={handleCloseModal}
            agentID={data?.agentID ?? ''}
            agentName={data?.agentName ?? ''}
            allowInvoiceDeal={data?.allowInvoiceDeal ?? false}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PopupConfirmCustomer;
